import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchFactionPieces, type FactionPieces } from '../api/catalog';
import {
  createSession,
  fetchRecipes,
  fetchRecipesFor,
  fetchTwists,
  generateMission,
  type RecipeView,
  type TwistView,
} from '../api/missions';
import { tidy } from './catalanText';
import { ca } from '../i18n/ca';
import { fixByRoute, refusalOf, type Refusal } from './newMissionErrors';
import { LoadProblemNote } from './LoadProblemNote';
import { problemOf, type LoadProblem } from './loadProblem';
import { RefusalDialog, type RefusalAction } from './RefusalDialog';
import { WarningLine } from './WarningLine';
import { FixedValue, HowItWorks } from './HelpFold';
import { QuickStart } from './NewMission';
import { inviteDraftEditors } from './inviteDraftEditors';
import {
  DECIDE_KEY,
  DRAFT_KEY,
  START_KEY,
  STEP,
  STEP2_KEY,
  patchStored,
  readStored,
  summariesOf,
  writeDraft,
  writeStored,
  type MissionDraftDecide,
  type MissionDraftStart,
  type MissionDraftStep1,
  type MissionDraftStep2,
} from './newMissionDraft';
import { PlannedButton, PlannedChip } from './Planned';
import type { PlannedId } from './plannedRegistry';
import { choicesOf, settingValue, settingsToSend, switchesOf } from './recipeSettings';
import {
  adminOnly,
  testingLabel,
  admitsIn,
  admitsOut,
  admitsStart,
  admitsSupport,
  greyReason,
  hourHint,
  hourOptions,
  isComing,
  isGreyed,
  isTesting,
  limitOf,
  matchesRecipeSearch,
  playersRange,
  weatherOptions,
} from './recipeRules';
import { Shell } from './Shell';
import { StartFromStepOne } from './StartFromStepOne';
import { useAuth } from './AuthContext';

/**
 * The wizard's own word for each way in the generator names (`startMeans`),
 * for a refusal's «Canvia l’entrada a …»: what the start step stores.
 */
const WAY_IN_OPTION: Record<string, string> = {
  helicopter: 'helicòpter',
  vehicle: 'vehicle',
  foot: 'a peu',
};

type Step2 = MissionDraftStep2;

const readDraft = readStored;

/** The platform's own bounds on a party, the only hard ones (a type's range is a recommendation). */
const PLAYERS_MIN = 1;
const PLAYERS_MAX = 64;

const hourLabel = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${h % 1 ? '30' : '00'}`;

/** The kept ways the type still admits, or null (the type's own) when none of them is. */
function admittedOrNull(
  list: string[] | undefined,
  admits: (o: string) => boolean,
): string[] | null {
  const kept = (list ?? []).filter(admits);
  return kept.length ? kept : null;
}

const blurbOf = (r: RecipeView) => r.blurb.replace(/%PLACE2?%/g, '…').replace(/%CAST%/g, '…');

/**
 * One recipe as a card: icon, title, the whole blurb, a footer of chips.
 * A coming one (the owner's rule, 2026-09-27: not ready, not in testing)
 * cannot be picked and says «Properament»; one in testing is picked and says
 * «En proves · N de M» (the test cards it has passed and needs; «En proves» alone
 * with no count); a ready one carries no chip; one this server, map or faction cannot play is greyed with the
 * reason and what would make it fit (ENGINE_DESIGN.md §2.20). An admin may
 * still pick a coming one the generator builds, as the platform lets an admin
 * generate it: it keeps its greyed look and says «(només administradors)».
 */
function RecipeCard({
  recipe,
  selected,
  onPick,
  isAdmin = false,
}: {
  recipe: RecipeView;
  selected: boolean;
  onPick: () => void;
  isAdmin?: boolean;
}) {
  // Two ways to be unavailable, and they are not the same thing: a type nobody
  // may pick yet, and one this server, map or faction cannot play.
  const coming = isComing(recipe);
  const testing = isTesting(recipe);
  const greyed = isGreyed(recipe);
  const admins = adminOnly(recipe, isAdmin);
  const why = greyed ? greyReason(recipe) : null;
  const off = (coming && !admins) || greyed;
  // The badge is said in the button's own name, not only shown: a colour is not a status.
  const comingBadge = admins
    ? `${ca.missionUi.comingSoon} ${ca.missionUi.adminsOnly}`
    : ca.missionUi.comingSoon;
  const testingText = testingLabel(recipe);
  const badge = coming ? comingBadge : testing ? testingText : null;
  return (
    <button
      type="button"
      className={`card recipe ${selected ? 'selected' : ''} ${greyed ? 'lacks' : ''} ${testing ? 'testing' : ''} ${admins ? 'admins-only' : ''}`}
      aria-pressed={off ? undefined : selected}
      aria-disabled={off || undefined}
      aria-label={badge ? `${recipe.title} (${badge})` : recipe.title}
      title={
        why
          ? why.text
          : coming
            ? ca.missionUi.comingSoonHint
            : testing
              ? ca.missionUi.testingHint
              : undefined
      }
      onClick={off ? undefined : onPick}
    >
      <span className="head">
        <i className={`bi bi-${recipe.icon || 'flag'}`} aria-hidden="true" />
        <strong>{recipe.title}</strong>
      </span>
      <span className="blurb">{blurbOf(recipe)}</span>
      {why && <span className="notice warn small">{why.text}</span>}
      {why?.fix && <span className="muted small">{why.fix}</span>}
      {!why && recipe.needs && !['bridge', 'road'].includes(recipe.needs) && (
        <span className="muted small">{ca.missionUi.needsGroundHint(recipe.needs)}</span>
      )}
      {!greyed &&
        (recipe.fallbacks ?? []).map((f) => (
          <span key={f.code} className="muted small">
            {ca.missionUi.fallbacksTitle}: {f.reason}
          </span>
        ))}
      <span className="foot">
        <span className="chip" title={ca.missionUi.playersRangeTitle}>
          <i className="bi bi-people" aria-hidden="true" />
          {ca.missionUi.playersRange(recipe.minPlayers, recipe.maxPlayers)}
        </span>
        <span className="chip" title={ca.missionUi.durationTitle}>
          <i className="bi bi-clock" aria-hidden="true" />
          {ca.missionUi.duration(recipe.minutes)}
        </span>
        {recipe.enemyStrength && (
          <span className="chip" title={ca.missionUi.strengthTitle}>
            <i className="bi bi-shield" aria-hidden="true" />
            {ca.missionUi.strengthNames[recipe.enemyStrength] ?? recipe.enemyStrength}
          </span>
        )}
        {coming && <span className="chip coming">{comingBadge}</span>}
        {testing && (
          <span className="chip testing" title={ca.missionUi.testingHint}>
            <i className="bi bi-cone-striped" aria-hidden="true" />
            {testingText}
          </span>
        )}
      </span>
    </button>
  );
}

/** Step 2 (UI doc 5.3): the mission type, how many play, when. */
export function NewMissionType() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const isAdmin = me?.roles.includes('ADMIN') ?? false;
  const step1 = readDraft<MissionDraftStep1>(DRAFT_KEY);
  // What this step said last time, so "Enrere" from a later step comes back to
  // the type that was chosen rather than to an empty form.
  const [saved] = useState(() => readDraft<Step2>(STEP2_KEY));
  const [recipes, setRecipes] = useState<RecipeView[]>([]);
  /** Why the types did not arrive, when they did not: never shown as a step with no type to pick. */
  const [recipesProblem, setRecipesProblem] = useState<LoadProblem | null>(null);
  const [recipesAsked, setRecipesAsked] = useState(0);
  /** What the creator typed to find a type by its name; empty shows every type. */
  const [search, setSearch] = useState('');
  const recipesRef = useRef<HTMLElement | null>(null);
  const [recipeId, setRecipeId] = useState(saved?.recipeId ?? '');
  // Sixteen, which is a night of the group's, not the recipe's ceiling: the
  // type step opened at 24 and every mission came out for a company (2026-09-11).
  const [players, setPlayers] = useState(saved?.players ?? 16);
  const [hour, setHour] = useState<string>(saved?.hour == null ? '' : String(saved.hour));
  const [weather, setWeather] = useState<string>(saved?.weather ?? '');
  // Who decides the secondaries, and which. The creator by default, with none
  // ticked: a night nobody chose a secondary for has none (the owner, 2026-09-18).
  // It used to be the seed's, and an ambush nobody asked for turned up in the
  // owner's own night (session 3gasy).
  const [secondaryBy, setSecondaryBy] = useState<'chance' | 'creator' | 'voteBefore'>(
    saved?.secondaryBy ?? 'creator',
  );
  const [secondaries, setSecondaries] = useState<string[] | null>(
    saved?.secondaryObjectives ?? null,
  );

  // The type comes before the map now (2026-09-13): every type is offered, each
  // saying what ground it needs, and the map step marks the terrains that lack
  // it. A terrain chosen earlier and kept in the draft still filters, so a
  // creator walking back sees what that terrain cannot host.
  const world = step1?.worldName;
  const hash = step1?.modSetHash;
  // What the server, and whatever later steps a walk back has already answered,
  // can play (ENGINE_DESIGN.md §2.18): our faction, the enemy, the era.
  const playerFaction = step1?.playerFactionClass ?? null;
  const enemyProfileId = step1?.enemyProfileId ?? null;
  const era = step1?.era ?? null;

  useEffect(() => {
    setRecipesProblem(null);
    fetchRecipesFor(world, hash, { playerFaction, enemyProfileId, era })
      .then((list) => {
        setRecipes(list);
        // A draft kept from before may hold an hour or a weather the type hides
        // (22:30 for a night_raid that starts before dawn): dropped back to the
        // type's own, so the field never shows a value it does not offer.
        const kept = list.find((r) => r.id === saved?.recipeId) ?? null;
        if (!kept) return;
        setHour((h) => (h !== '' && !hourOptions(kept).includes(Number(h)) ? '' : h));
        setWeather((w) => (w !== '' && !weatherOptions(kept).includes(w) ? '' : w));
      })
      .catch((err) => setRecipesProblem(problemOf(err)));
  }, [world, hash, playerFaction, enemyProfileId, era, saved?.recipeId, recipesAsked]);

  if (!step1) {
    return <StartFromStepOne />;
  }

  // Greyed types stay in the list, with their reason; only the coming ones go below.
  // A search only narrows what is listed: the type already chosen stays chosen.
  const shown = recipes.filter((r) => matchesRecipeSearch(r, search));
  const available = shown.filter((r) => !isComing(r));
  const planned = shown.filter((r) => isComing(r));
  const chosen =
    recipes.find(
      (r) => r.id === recipeId && (!isComing(r) || adminOnly(r, isAdmin)) && !isGreyed(r),
    ) ?? null;
  // A type is written for so many people, and says so; the number used to go
  // through unsaid (F13), then was a wall. Since the owner's rule of 2026-09-28
  // it is a recommendation: outside it the step warns and lets the creator on,
  // and the generator scales the enemy and says it in the mission's warnings.
  const range = chosen ? playersRange(chosen) : null;
  const fits = !range || (players >= range.min && players <= range.max);
  // The platform's own bounds, the only hard ones: at least one, at most a slot list's.
  const playable = players >= PLAYERS_MIN && players <= PLAYERS_MAX;
  // Only what the chosen type admits (MECHANICS.md R17): the rest is hidden, not greyed.
  const hours = hourOptions(chosen);
  const weathers = weatherOptions(chosen);
  const hint = hourHint(chosen);

  /** A type, and the players, the hour and the weather brought inside what it admits. */
  function pick(r: RecipeView) {
    if (r.id !== recipeId) {
      // Another type carries other secondaries: the choice made for the old one goes,
      // back to the creator's with none ticked.
      setSecondaryBy('creator');
      setSecondaries(null);
    }
    setRecipeId(r.id);
    // The number the creator gave is kept: the type's own range is a
    // recommendation, said under the field, never a correction.
    // A15: a night-only type keeps a night hour chosen for another type, but a
    // daylight one is dropped back to the seed's — the generator would have
    // replaced it anyway. The same for any hour or weather the type does not admit.
    if (hour !== '' && !hourOptions(r).includes(Number(hour))) {
      setHour('');
    }
    if (weather !== '' && !weatherOptions(r).includes(weather)) {
      setWeather('');
    }
  }

  function next() {
    if (!chosen || !playable) return;
    const draft: Step2 = {
      recipeId,
      recipeTitle: chosen.title,
      players,
      hour: hour === '' ? null : Number(hour),
      weather: weather || null,
      // What the next step starts its squad from: this type's own table, and
      // nothing when a mod set's recipe list is older than the field.
      recipeRoles: chosen.roles ?? [],
      secondaryBy,
      // Ids for a creator's choice or a vote's; the seed's names none.
      secondaryObjectives: secondaryBy === 'chance' ? null : (secondaries ?? []),
      needs: chosen.needs ?? null,
      enemyRule: chosen.constraints?.enemy ?? null,
    };
    writeStored(STEP2_KEY, draft);
    // Another type proposes another squad. The table the next step kept was
    // the old type's, and it won over the new one's proposal (F10).
    if (saved?.recipeId !== recipeId) writeDraft({ roles: null });
    navigate('/new/map');
  }

  // Beside the recipes rather than under them: the number of players only
  // means something once a type says how many it is written for.
  const sizeCard = (
    <section className="card form" aria-label={ca.missionUi.sizeAndTime}>
      <h2>{ca.missionUi.sizeAndTime}</h2>
      <div className="fields">
        <label>
          {ca.playersCount}
          <input
            name="players"
            type="number"
            min={PLAYERS_MIN}
            max={PLAYERS_MAX}
            value={players}
            aria-invalid={playable ? undefined : true}
            aria-describedby={range ? 'players-range' : undefined}
            onChange={(e) => setPlayers(Number(e.target.value))}
          />
        </label>
        <label>
          {ca.timeOfDay}
          <select name="hour" value={hour} onChange={(e) => setHour(e.target.value)}>
            {/* A type that works its own hour out (night_raid: dawn less two and a half
                hours) says so; any other hour it offers is one it keeps. */}
            <option value="">
              {chosen?.constraints?.hours ? ca.missionUi.hourFromType : ca.timeSeeded}
            </option>
            {hours.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
          {hint && <p className="muted small">{hint}</p>}
        </label>
        <label>
          {ca.weather}
          <select name="weather" value={weather} onChange={(e) => setWeather(e.target.value)}>
            <option value="">{ca.timeSeeded}</option>
            {weathers.map((k) => (
              <option key={k} value={k}>
                {ca.weatherNames[k] ?? k}
              </option>
            ))}
          </select>
        </label>
      </div>
      {range && (
        <p
          id="players-range"
          className={fits ? 'muted small' : 'notice warn small'}
          role={fits ? undefined : 'status'}
        >
          {fits
            ? ca.missionUi.playersRangeHint(range.min, range.max)
            : ca.missionUi.playersOutOfRange(range.min, range.max)}
        </p>
      )}
    </section>
  );

  // The optional objectives this type may carry, beside the recipes: the matrix
  // row used to say who decides them with nothing to decide between (F6).
  const offered = chosen?.secondaries ?? [];
  const s2 = ca.missionUi.secondaries;
  function chooseSecondaryBy(b: 'chance' | 'creator' | 'voteBefore') {
    setSecondaryBy(b);
    // A vote needs something to vote between, so it starts with every one offered;
    // the creator's own list starts empty, and empty means none.
    if (b === 'voteBefore') setSecondaries((current) => current ?? offered.map((s) => s.id));
  }
  function toggleSecondary(id: string) {
    setSecondaries((current) => {
      const list = current ?? [];
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });
  }
  const secondariesCard = chosen && (
    <section className="card form" aria-label={s2.title}>
      <h2>{s2.title}</h2>
      {offered.length === 0 ? (
        <p className="muted small">{s2.none}</p>
      ) : (
        <>
          <div className="row wrap" role="radiogroup" aria-label={s2.who}>
            {(
              [
                ['chance', s2.bySeed],
                ['creator', s2.these],
                ['voteBefore', s2.vote],
              ] as const
            ).map(([k, label]) => (
              <button
                type="button"
                key={k}
                role="radio"
                aria-checked={secondaryBy === k}
                className={`chip toggle ${secondaryBy === k ? 'on' : ''}`}
                onClick={() => chooseSecondaryBy(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <ul className="twist-choices">
            {offered.map((s) => (
              <li key={s.id}>
                <label className="check">
                  {secondaryBy !== 'chance' && (
                    <input
                      type="checkbox"
                      name={`secondary-${s.id}`}
                      checked={(secondaries ?? []).includes(s.id)}
                      onChange={() => toggleSecondary(s.id)}
                    />
                  )}
                  <span>
                    <strong>{s.title}</strong>{' '}
                    {s.own && <span className="chip">{ca.missionUi.ownOptional}</span>}{' '}
                    <span className="chip" title={s2.chanceTitle}>
                      {Math.round(s.chance * 100)}%
                    </span>{' '}
                    {s.foundByIntel && (
                      <i
                        className="bi bi-lock-fill"
                        role="img"
                        aria-label={s2.found}
                        title={s2.found}
                      />
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="muted small">
            {secondaryBy === 'chance'
              ? s2.hintSeed
              : secondaryBy === 'creator'
                ? s2.hintThese
                : s2.hintVote}
          </p>
        </>
      )}
    </section>
  );

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <Link className="button" to="/new">
            {ca.back}
          </Link>
          <button type="button" className="primary" disabled={!chosen || !playable} onClick={next}>
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart
        step={STEP.type}
        summaries={summariesOf(step1)}
        aside={
          <>
            {sizeCard}
            {secondariesCard}
          </>
        }
      >
        <section className="card form" aria-label={ca.missionType} ref={recipesRef}>
          <div className="row between">
            <h2>{ca.missionType}</h2>
            {recipes.length > 0 && (
              <div className="row type-search">
                <label className="search">
                  <input
                    type="search"
                    name="typeSearch"
                    placeholder={ca.missionUi.searchType}
                    aria-label={ca.missionUi.searchType}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && search !== '') {
                        e.preventDefault();
                        setSearch('');
                      } else if (e.key === 'ArrowDown') {
                        // From the box straight into the cards, which are plain buttons.
                        const first = recipesRef.current?.querySelector<HTMLButtonElement>(
                          '.recipes button.recipe:not([aria-disabled])',
                        );
                        if (first) {
                          e.preventDefault();
                          first.focus();
                        }
                      }
                    }}
                  />
                </label>
                {search !== '' && (
                  <button
                    type="button"
                    className="link"
                    title={ca.missionUi.searchTypeClear}
                    onClick={() => setSearch('')}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" /> {ca.missionUi.searchTypeClear}
                  </button>
                )}
              </div>
            )}
          </div>
          {recipesProblem && (
            <LoadProblemNote
              problem={recipesProblem}
              what="recipes"
              onRetry={() => setRecipesAsked((n) => n + 1)}
            />
          )}
          {search.trim() !== '' && shown.length === 0 && (
            <p className="muted small" role="status">
              {ca.missionUi.searchTypeNone(search.trim())}
            </p>
          )}
          <div className="recipes">
            {available.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={r}
                selected={recipeId === r.id}
                onPick={() => pick(r)}
              />
            ))}
          </div>
          {planned.length > 0 && (
            <>
              <h3 className="muted">{ca.missionUi.comingSoon}</h3>
              <div className="recipes" aria-label={ca.missionUi.comingSoon}>
                {planned.map((r) => (
                  <RecipeCard
                    key={r.id}
                    recipe={r}
                    isAdmin={isAdmin}
                    selected={recipeId === r.id && adminOnly(r, isAdmin)}
                    onPick={() => pick(r)}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      </QuickStart>
    </Shell>
  );
}

/** Who may decide a row of the votes (UI doc 5.3, study 5.13). */
const BY = ['creator', 'voteBefore', 'voteDuring', 'chance'] as const;
type By = (typeof BY)[number];

/** Nobody votes on a surprise: a twist is the seed's, or whoever directs the night. */
const TWIST_BY = ['chance', 'creator'] as const;

/** The decisions the backend honours today; the rest stay at "the creator" and are marked planned. */
const LIVE = ['time', 'weather', 'twist'] as const;
type LiveKey = (typeof LIVE)[number];

/** Who decides each live row until somebody says otherwise. */
const DEFAULT_BY: Record<LiveKey, By> = {
  time: 'creator',
  weather: 'creator',
  // A twist is a surprise: it is either the seed's or whoever directs the night,
  // never a vote. The generator reads "creator" as "nothing fires on its own".
  twist: 'chance',
};

/** A row of the decision table the design asks for and nobody can use yet, with its registry entry. */
type PlannedRow = DecisionRow & { plannedId: PlannedId };

interface DecisionRow {
  key: string;
  label: string;
  value: string;
  /** Where a row decided on another step is changed, for the ones shown here read-only. */
  change?: { to: string; label: string };
}

/**
 * The last step, «Crea» (UI doc 5.3; rebuilt 2026-10-12 at the owner's word):
 * one column — the title, when it is played, the hour and the weather, the
 * supports, the twists, the votes folded away, and a summary with the button
 * that makes the mission.
 *
 * Every control is in one of three states, and the type of mission is what
 * says which (`RecipeView.limits`, RecipeLimits on the platform): free, drawn
 * as it always was; limited, drawn with only what the type admits and one
 * line saying the range and why; fixed, a lock with the value and the reason,
 * never edited and never put to a vote. The hour and the weather are the type
 * step's own answers (STEP2_KEY): this step edits that same draft, so the two
 * screens cannot disagree.
 */
export function NewMissionDecide() {
  const navigate = useNavigate();
  const step1 = readDraft<MissionDraftStep1>(DRAFT_KEY);
  const step2 = readDraft<Step2>(STEP2_KEY);
  // A twist's text is a template the generator fills with the night's places and
  // cast; here it reads as the creator will see it, not as "%PLACE%". Tidied the
  // way the platform tidies its own — "prop de un nucli proper" is not what the
  // published twist says, and a sentence that begins with a placeholder still
  // begins with a capital (F14).
  const twistBody = (body: string) =>
    tidy(
      body
        .replace(/%PLACE2%/g, ca.missionUi.twists.otherPlace)
        .replace(/%PLACE%/g, step1?.preferredPlace || ca.missionUi.twists.somePlace)
        .replace(/%CAST%/g, ca.missionUi.twists.someone),
    );
  // Where the night starts and the ways in and out: step 7's, sent as it left them.
  const start = readDraft<MissionDraftStart>(START_KEY);
  const recipeId = step2?.recipeId;
  // What this step held before an "Enrere". The supports are the recipe's own
  // until somebody edits them, so they come back only when they were filled
  // from this same recipe.
  const [saved] = useState(() => {
    const d = readDraft<MissionDraftDecide>(DECIDE_KEY);
    return d && d.recipeId !== recipeId ? { ...d, supports: null } : d;
  });
  // Which support menu the players will have. It decides whether this card asks
  // for a number the game counts down or a tick Simplex reads as "mount it"
  // (QUICK_START_REVIEW.md F16); step 1 wrote it from the preset's readiness.
  const simplex = step1?.supportsSystem === 'simplex';
  const [recipe, setRecipe] = useState<RecipeView | null>(null);
  const [by, setBy] = useState<Record<LiveKey, By>>(() => ({
    ...DEFAULT_BY,
    ...(saved?.by as Partial<Record<LiveKey, By>> | undefined),
  }));
  /** Null until it is filled from the recipe. */
  const [supports, setSupports] = useState<Record<string, number> | null>(saved?.supports ?? null);
  const [title, setTitle] = useState(saved?.title ?? '');
  // A night that started by itself once enough players were on is no longer
  // offered (the owner, 2026-10-19): a draft that chose it is started by hand.
  const [whenMode, setWhenMode] = useState<'now' | 'date' | 'onStart'>(
    saved?.whenMode === 'onPlayers' ? 'onStart' : (saved?.whenMode ?? 'now'),
  );
  const [whenAt, setWhenAt] = useState(saved?.whenAt ?? '');
  /** Born at the group's level; private is the author's choice (the owner, 2026-10-14). */
  const [visibility, setVisibility] = useState<'PUBLIC' | 'PRIVATE'>(saved?.visibility ?? 'PUBLIC');
  // The hour and the weather are the type step's (STEP2_KEY), where they still
  // are: held here to draw them, and every change is written back to that draft.
  const [hour, setHourHeld] = useState<number | null>(step2?.hour ?? null);
  const [weather, setWeatherHeld] = useState<string | null>(step2?.weather ?? null);
  /** An hour kept from an older draft that the type does not admit: dropped, and said. */
  const [hourDropped, setHourDropped] = useState<number | null>(null);
  function setHour(h: number | null) {
    setHourHeld(h);
    patchStored<Step2>(STEP2_KEY, { hour: h });
  }
  function setWeather(w: string | null) {
    setWeatherHeld(w);
    patchStored<Step2>(STEP2_KEY, { weather: w });
  }
  // The server is step 1's: it is what decided the mods, and this step only says it.
  const serverId = step1?.serverId ?? null;
  const [twists, setTwists] = useState<TwistView[]>([]);
  // Null until the creator touches the list: the seed and the recipe decide.
  // None ticked is a night with no twist (the owner, 2026-09-18): the list starts
  // empty and travels as it is. It used to start as "nobody said", which the
  // generator reads as the recipe's own twists drawn against the seed.
  const [chosenTwists, setChosenTwists] = useState<string[]>(saved?.chosenTwists ?? []);
  // The type's own switches and choices (release D, D6.3): off and the default
  // until somebody says otherwise, never rolled.
  const [settings, setSettings] = useState<Record<string, string>>(saved?.settings ?? {});

  // Everything above, kept as it changes: the one step that used to lose it all
  // on "Enrere" (F11).
  useEffect(() => {
    if (!recipeId) return;
    writeStored<MissionDraftDecide>(DECIDE_KEY, {
      recipeId,
      by,
      supports,
      chosenTwists,
      settings,
      whenMode,
      whenAt,
      visibility,
      serverId,
      title,
    });
  }, [
    recipeId,
    by,
    supports,
    chosenTwists,
    settings,
    whenMode,
    whenAt,
    visibility,
    serverId,
    title,
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Refusal | null>(null);
  /**
   * Step 8 (ENGINE_DESIGN.md §2.20): a mission that generated with warnings
   * stays here long enough to say them, with the way on to where it would
   * have gone. One with none goes straight on, as it always did.
   */
  const [generated, setGenerated] = useState<{
    warnings: string[];
    to: string;
    session: boolean;
    /** Members the wizard's list named whom the platform could not add as editors. */
    uninvited: string[];
  } | null>(null);
  /** What our faction can be supported by; null until it answers, and when nobody has read the mod set. */
  const [pieces, setPieces] = useState<FactionPieces | null>(null);

  // A support is offered only when our faction has a piece for it, and a drone
  // only in a modern night (SERVER_SETTING_STUDY.md).
  useEffect(() => {
    if (!step1?.modSetHash) return;
    let live = true;
    fetchFactionPieces(step1.modSetHash, {
      faction: step1.playerFactionClass,
      side: step1.playerSide,
      era: step1.era,
      seats: step1.squadSize ?? 8,
      // What the server plays its supports with: a support nothing there can carry is off too.
      system: step1.supportsSystem,
    })
      .then((p) => {
        if (live) setPieces(p);
      })
      .catch(() => {
        if (live) setPieces(null);
      });
    return () => {
      live = false;
    };
  }, [
    step1?.modSetHash,
    step1?.playerFactionClass,
    step1?.playerSide,
    step1?.era,
    step1?.squadSize,
    step1?.supportsSystem,
  ]);

  // What the recipe proposed, or another night left ticked, that cannot be given: off.
  useEffect(() => {
    if (!pieces || !supports) return;
    const gone = Object.keys(supports).filter(
      (k) => (supports[k] ?? 0) > 0 && pieces.supports[k] === false,
    );
    if (gone.length === 0) return;
    setSupports((s) => {
      const next = { ...(s ?? {}) };
      for (const k of gone) next[k] = 0;
      return next;
    });
  }, [pieces, supports]);

  // The chosen type judged against everything decided since it was picked — the
  // map, both factions, the era — so the last step says before generating what
  // the generator would refuse (ENGINE_DESIGN.md §2.18).
  useEffect(() => {
    if (!step2) return;
    (step1?.modSetHash
      ? fetchRecipesFor(step1.worldName, step1.modSetHash, {
          playerFaction: step1.playerFactionClass ?? null,
          enemyProfileId: step1.enemyProfileId ?? null,
          era: step1.era ?? null,
        })
      : fetchRecipes()
    )
      .then((list) => {
        const r = list.find((x) => x.id === step2.recipeId) ?? null;
        setRecipe(r);
        if (r) {
          // The recipe's own, unless this step already holds what somebody set for it.
          setSupports((s) => s ?? { ...r.supports });
          // An hour or a weather kept from before that the type hides goes back to
          // the type's own, in the draft the type step reads too; the hour is said.
          if (step2.hour != null && !hourOptions(r).includes(step2.hour)) {
            setHourDropped(step2.hour);
            setHour(null);
          }
          if (step2.weather && !weatherOptions(r).includes(step2.weather)) setWeather(null);
        }
      })
      .catch(() => setRecipe(null));
  }, [step2?.recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTwists()
      .then(setTwists)
      .catch(() => setTwists([]));
  }, []);

  // The per-recipe allowed-twists rule (docs/design/scripts/night_raid_contract.md
  // §C; ENGINE_DESIGN.md §2.19): a twist this recipe does not accept is not shown
  // at all (MECHANICS.md R17: hidden, not greyed), and a choice left ticked from
  // a recipe the creator changed away from is dropped here rather than sent to
  // the generator to be refused.
  useEffect(() => {
    if (!recipe?.twists || chosenTwists.length === 0) return;
    const admitted = recipe.twists;
    const kept = chosenTwists.filter((id) => admitted.includes(id));
    if (kept.length !== chosenTwists.length) setChosenTwists(kept);
  }, [recipe, chosenTwists]);

  if (!step1 || !step2) {
    return <StartFromStepOne />;
  }

  const t = ca.missionUi.decide;
  // What the type leaves of each control (RecipeView.limits): free, limited, fixed.
  const hourLimit = limitOf(recipe, 'hour');
  const weatherLimit = limitOf(recipe, 'weather');
  const supportsLimit = limitOf(recipe, 'supports');
  const twistsLimit = limitOf(recipe, 'twists');
  const wayOutLimit = limitOf(recipe, 'wayOut');
  const hourFixed = hourLimit?.state === 'fixed';
  const weatherFixed = weatherLimit?.state === 'fixed';
  // Only what the chosen type admits (MECHANICS.md R17): the rest is not drawn.
  const hours = hourOptions(recipe);
  const weathers = weatherOptions(recipe);
  const hourReason = hourLimit?.reason ?? hourHint(recipe);
  /** Who decides a live row: the creator, always, for one the type fixes. */
  const whoDecides = (key: LiveKey): By =>
    (key === 'time' && hourFixed) || (key === 'weather' && weatherFixed) ? 'creator' : by[key];
  const hourAsked = whoDecides('time') === 'creator' && !hourFixed ? hour : null;
  const weatherName = (w: string) => ca.weatherNames[w] ?? w;
  /** The weather the type fixes is its one admitted value; else the creator's, when it is theirs to say. */
  const weatherAsked = weatherFixed
    ? (weatherLimit?.values[0] ?? null)
    : whoDecides('weather') === 'creator'
      ? weather
      : null;

  /** The value column: the chosen value while the creator decides, else who will. */
  function valueFor(key: LiveKey, chosen: string): string {
    const b = whoDecides(key);
    if (b === 'creator') return chosen;
    return b === 'chance' ? ca.missionUi.decisionTable.byChance : ca.missionUi.decisionTable.byVote;
  }
  const hourWords =
    hour != null
      ? hourLabel(hour)
      : recipe?.constraints?.hours
        ? ca.missionUi.hourFromType
        : ca.timeSeeded;
  const weatherWords = weather ? weatherName(weather) : ca.timeSeeded;

  // What can be put to a vote today. A row the type fixes is not among them.
  const voteRows: DecisionRow[] = [
    { key: 'time', label: ca.decisionKeys.time, value: valueFor('time', hourWords) },
    { key: 'weather', label: ca.decisionKeys.weather, value: valueFor('weather', weatherWords) },
    {
      key: 'twist',
      label: ca.decisionKeys.twist,
      value: valueFor('twist', ca.missionUi.decisionTable.twistByHand),
    },
  ].filter((r) => !(r.key === 'time' && hourFixed) && !(r.key === 'weather' && weatherFixed));
  const fixedNames = [
    hourFixed ? ca.decisionKeys.time : null,
    weatherFixed ? ca.decisionKeys.weather : null,
  ].filter((n): n is string => n !== null);
  // What the votes will ask one day: shown, disabled, each with its «Pendent».
  const plannedVotes: PlannedRow[] = [
    {
      key: 'map',
      plannedId: 'vote-map',
      label: ca.missionUi.decisionTable.map,
      value: step1.worldDisplayName ?? step1.worldName,
    },
    {
      key: 'enemy',
      plannedId: 'vote-enemy',
      label: ca.missionUi.decisionTable.enemy,
      value: step1.enemyName ?? step1.enemyFactionClass ?? '—',
    },
    {
      key: 'type',
      plannedId: 'vote-mission-type',
      label: ca.missionUi.decisionTable.type,
      value: recipe?.title ?? '—',
    },
  ];
  const plannedRows: PlannedRow[] = [
    {
      key: 'nextChapter',
      plannedId: 'vote-next-chapter',
      label: ca.missionUi.decisionTable.nextChapter,
      value: '—',
    },
  ];
  // The ways in and out are step 7's: said here with who decides them, and
  // changed there. They were not on this step at all, although the published
  // mission carried both (QUICK_START_REVIEW.md F4).
  const waysValue = (options: string[] | undefined, who: string | undefined) =>
    !options?.length
      ? '—'
      : options.length === 1
        ? options[0]
        : `${options.join(' o ')} · ${ca.decidedBy[who ?? 'creator'] ?? who}`;
  // The secondaries are the type step's since 2026-09-10 (F6), said here the same way.
  const s2 = ca.missionUi.secondaries;
  const chosenSecondaries = step2.secondaryObjectives?.length ?? 0;
  const atStart = { to: '/new/start', label: ca.missionUi.decisionTable.changeAtStart };
  // Decided on an earlier step: a line each in the summary, with the way back.
  const elsewhere: DecisionRow[] = [
    {
      key: 'secondaryObjective',
      label: ca.decisionKeys.secondaryObjective,
      value:
        step2.secondaryBy === 'chance'
          ? s2.rowSeed
          : step2.secondaryBy === 'voteBefore'
            ? s2.rowVote(chosenSecondaries)
            : s2.rowChosen(chosenSecondaries),
      change: { to: '/new/type', label: ca.missionUi.decisionTable.changeAtType },
    },
    {
      key: 'insertion',
      label: ca.decisionKeys.insertion,
      value: waysValue(start?.insertionMeans, start?.insertionBy),
      change: atStart,
    },
    // The commander of the night is the session's, not the mission's: named or put
    // to a vote on the direction page, once the night has a session.
    {
      key: 'commander',
      label: ca.decisionKeys.commander,
      value: ca.missionUi.decisionTable.commanderAtSession,
    },
    // The way out the type decides (R2) is its own sentence, with nothing to change.
    wayOutLimit?.state === 'fixed'
      ? {
          key: 'extraction',
          label: ca.decisionKeys.extraction,
          value: wayOutLimit.reason ?? wayOutLimit.words ?? '—',
        }
      : {
          key: 'extraction',
          label: ca.decisionKeys.extraction,
          value: waysValue(start?.extractionMeans, start?.extractionBy),
          change: atStart,
        },
  ];

  // The type's own settings (release D, D6.3): switches under «Girs», choices apart.
  const switches = switchesOf(recipe);
  const choices = choicesOf(recipe);
  const switchedOn = switches.filter((s) => settingValue(s, settings) === 'on').map((s) => s.title);
  // The per-recipe allowed-twists rule (contract §C.2, ENGINE_DESIGN.md §2.19):
  // only the twists this type accepts are offered. Before the type has answered, all are.
  const offeredTwists = twists.filter((tw) => !recipe?.twists || recipe.twists.includes(tw.id));
  const noTwists = recipe?.twists != null && recipe.twists.length === 0;

  function toggleTwist(id: string) {
    setChosenTwists((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  }

  /** The supports as they travel: only the ones the type admits, and none our faction has no piece for. */
  const sentSupports = supports
    ? Object.fromEntries(
        Object.entries(supports)
          .filter(([k]) => admitsSupport(recipe, k))
          .map(([k, n]) => [k, pieces?.supports[k] === false ? 0 : n]),
      )
    : supports;

  async function generate() {
    // One mission (and one session) per visit: once it has generated, the only
    // way on is the panel's own link, never a second mission behind it.
    if (busy || generated) return;
    setBusy(true);
    setError(null);
    try {
      const mission = await generateMission(
        {
          recipeId: step2!.recipeId,
          worldClass: step1!.worldName,
          modSetHash: step1!.modSetHash,
          era: step1!.era ?? null,
          // What step 5 said, and what the wizard used to hard-code: west, NATO,
          // whatever the recipe thought a squad was.
          playerSide: step1!.playerSide ?? 'west',
          playerFaction: step1!.playerFactionClass ?? null,
          // Two profiles can share a class (the 1RA and the USMC it was cloned from): which one.
          playerProfileId: step1!.playerProfileId ?? null,
          enemyProfileId: step1!.enemyProfileId,
          players: step2!.players,
          // A value the type hides is never held here; one it fixes is the type's own to work out.
          hour: hourAsked != null && hours.includes(hourAsked) ? hourAsked : null,
          weather: weatherAsked && weathers.includes(weatherAsked) ? weatherAsked : null,
          // Only the supports the type admits: the rest were never shown. And one our
          // faction has no piece for is sent as none, as its box shows it: the effect
          // above that zeroes it runs a moment after the box is drawn.
          supports: sentSupports,
          // One key per row of the votes, named as the generator names it. The twist row
          // used to be sent as `insertion`, so the toggle changed who decided the way in
          // and nothing at all decided the twists. The ways in and out are step 7's.
          decisions: {
            twist: by.twist,
            secondaryObjective: step2!.secondaryBy ?? 'creator',
            time: whoDecides('time'),
            weather: whoDecides('weather'),
            insertion: start?.insertionBy ?? 'creator',
            extraction: start?.extractionBy ?? 'creator',
          },
          insertionOptions: admittedOrNull(start?.insertionMeans, (o) => admitsIn(recipe, o)),
          // Back to the base the same way unless the step said otherwise; only then the ways out count.
          extractionAtBase: start?.returnToBase ?? true,
          // The orders come with the briefing unless the step asked for the folder on the table.
          ordersFolder: start?.ordersFolder ?? false,
          extractionOptions:
            (start?.returnToBase ?? true)
              ? null
              : admittedOrNull(start?.extractionMeans, (o) => admitsOut(recipe, o)),
          // The type step's choice: ids for a creator's or a vote's, none for the seed's.
          secondaryObjectives:
            step2!.secondaryBy === 'chance' ? null : (step2!.secondaryObjectives ?? []),
          startFrom: start?.startFrom === 'sea' ? 'sea' : 'land',
          // The creator's own point for the base, when the start step took one.
          startAt: start?.startFrom === 'point' ? (start.startAt ?? null) : null,
          seed: null,
          title: title || null,
          // Exactly the ticked ones; an empty array is a night with no turns.
          twists: chosenTwists,
          // Only what this type declares and was set away from its default.
          settings: settingsToSend(recipe, settings),
          preferredPlace: step1!.preferredPlace ?? null,
          enemyStrength: step1!.enemyStrength ?? null,
          enemySkill: step1!.enemySkill ?? null,
          enemiesFindBodies: step1!.enemiesFindBodies ?? null,
          squadSize: step1!.squadSize ?? null,
          roles: step1!.roles?.length ? step1!.roles : null,
          aircrew: step1!.aircrew ?? null,
          // Exactly the list step 7 showed; the generator adds none of its own (F5).
          // No base composition is named: the base follows the start (F2).
          playerVehicles: start?.vehicles?.length ? start.vehicles : null,
          enemyDefences: step1!.enemyDefences ?? null,
          intel: {
            area: step1!.intelArea ?? true,
            strongpoints: step1!.intelStrongpoints ?? true,
            quality: step1!.intelQuality ?? 'reasonable',
            marks: step1!.intelMarks ?? 2,
            falseMarks: step1!.intelFalse ?? 1,
          },
        },
        // The mission remembers where it was made to be played, and its page proposes it.
        serverId ?? null,
        visibility,
      );
      // The members named in the editors' list can only be added now that the mission exists.
      const uninvited = await inviteDraftEditors(mission.code);
      let to = `/m/${mission.code}`;
      if (whenMode === 'date' && whenAt) {
        const session = await createSession(
          mission.code,
          serverId ?? null,
          new Date(whenAt).toISOString(),
        );
        to = `/s/${session.session.code}`;
      } else if (whenMode === 'onStart') {
        // A session with no date: it exists, it has its server, and whoever
        // created it starts it by hand from the session page.
        const session = await createSession(mission.code, serverId ?? null, null);
        to = `/s/${session.session.code}`;
      }
      const warnings = mission.definition?.warnings ?? [];
      if (warnings.length > 0 || uninvited.length > 0) {
        setGenerated({ warnings, to, session: to.startsWith('/s/'), uninvited });
      } else {
        navigate(to);
      }
    } catch (err) {
      setError(refusalOf(err));
    } finally {
      setBusy(false);
    }
  }

  const closeRefusal = () => setError(null);
  /**
   * What a refusal's dialog offers to do (the owner, 2026-10-11): each thing the
   * platform worked out would work, as a button that does it — the way in set
   * and its step opened, the objective changed, the start step for a point of
   * the creator's own — and only what this type admits (MECHANICS.md R17).
   */
  const refusalActions = (refusal: Refusal): RefusalAction[] => {
    const tr = ca.missionUi.refusal;
    const out: RefusalAction[] = [];
    const alternatives = refusal.alternatives ?? [];
    for (const alt of alternatives) {
      if (alt.kind === 'means' && alt.value) {
        const option = WAY_IN_OPTION[alt.value];
        if (!option || !admitsIn(recipe, option)) continue;
        out.push({
          key: `means:${alt.value}`,
          label: tr.changeWayIn(alt.value),
          run: () => {
            // The start step proposes the vehicles again for a way in it was not given before.
            patchStored<MissionDraftStart>(START_KEY, { insertionMeans: [option] });
            navigate('/new/start');
          },
        });
      } else if (alt.kind === 'objective' && alt.value) {
        const name = alt.value;
        out.push({
          key: `objective:${name}`,
          label: tr.changeObjectiveTo(name),
          run: () => {
            writeDraft({ preferredPlace: name });
            navigate('/new/map');
          },
        });
      } else if (alt.kind === 'startPoint' && admitsStart(recipe, 'point')) {
        out.push({
          key: 'startPoint',
          label: tr.pickStartPoint,
          run: () => navigate('/new/start'),
        });
      }
    }
    if (alternatives.length > 0) {
      out.push({
        key: 'otherObjective',
        label: tr.otherObjective,
        run: () => navigate('/new/map'),
      });
    }
    return out;
  };
  const summaries = summariesOf(step1, step2, start);
  const fixLink = (fixBy: string | null | undefined) => {
    const to = fixByRoute(fixBy, step1.worldName);
    return to ? (
      <Link className="small" to={to}>
        {ca.missionUi.fixByLink[fixBy ?? ''] ?? ca.missionUi.fixBy[fixBy ?? '']}
      </Link>
    ) : null;
  };

  const isLive = (key: string): key is LiveKey => (LIVE as readonly string[]).includes(key);
  const supportName = (k: string) => (ca.supportNames as Record<string, string>)[k] ?? k;
  const supportRules = Object.entries(recipe?.constraints?.supportRules ?? {});
  // The supports a creator may tick: the type admits them and has no rule of its own on them.
  const offeredSupports = Object.keys(ca.supportNames)
    .filter((k) => admitsSupport(recipe, k))
    .filter((k) => !recipe?.constraints?.supportRules?.[k]);
  // The rest of the catalogue: shown too, off and padlocked, with the reason, so a creator
  // who expects artillery is told this type does not admit it rather than finding it gone.
  const lockedSupports = Object.keys(ca.supportNames)
    .filter((k) => !admitsSupport(recipe, k))
    .filter((k) => !recipe?.constraints?.supportRules?.[k]);
  const supportsCount =
    supportRules.length +
    offeredSupports.filter((k) => pieces?.supports[k] !== false && (supports?.[k] ?? 0) > 0).length;

  // The whole mission in one line, above the button that makes it.
  const summaryLine = [
    recipe?.title ?? step2.recipeTitle ?? null,
    step1.worldDisplayName ?? step1.worldName,
    step1.enemyName ? t.contra(step1.enemyName) : null,
    ca.missionUi.railPlayers(step2.players),
    hourFixed
      ? (hourLimit?.words ?? null)
      : whoDecides('time') !== 'creator'
        ? `${ca.decisionKeys.time.toLowerCase()}: ${ca.decidedBy[whoDecides('time')].toLowerCase()}`
        : hour != null
          ? t.hourOf(hourLabel(hour))
          : recipe?.constraints?.hours
            ? t.hourFromType
            : t.hourSeeded,
    weatherFixed
      ? weatherAsked
        ? weatherName(weatherAsked).toLowerCase()
        : null
      : whoDecides('weather') !== 'creator'
        ? `${ca.decisionKeys.weather.toLowerCase()}: ${ca.decidedBy[whoDecides('weather')].toLowerCase()}`
        : weather
          ? weatherName(weather).toLowerCase()
          : t.weatherSeeded,
    t.supports(supportsCount),
    chosenTwists.length === 0
      ? ca.missionUi.twists.none.toLowerCase()
      : ca.missionUi.twists.chosen(chosenTwists.length),
    (whenMode === 'date'
      ? ca.missionUi.when.dateTime
      : whenMode === 'onStart'
        ? ca.missionUi.when.onStart
        : ca.missionUi.when.now
    ).toLowerCase(),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <Link className="button" to="/new/start">
            {ca.back}
          </Link>
          <PlannedButton id="wizard-open-designer">{ca.missionUi.openDesigner}</PlannedButton>
        </>
      }
    >
      <QuickStart step={STEP.decide} summaries={summaries}>
        {error && (
          <RefusalDialog
            text={error.text}
            lead={error.param ? ca.missionUi.paramName(error.param) : null}
            detail={error.detail}
            actions={refusalActions(error)}
            link={fixLink(error.fixBy)}
            onClose={closeRefusal}
          />
        )}
        {recipe?.compatible === false && (
          <div className="notice warn" role="alert">
            <strong>{ca.missionUi.notPlayable}</strong>
            <ul>
              {(recipe.reasons ?? []).map((r) => (
                <li key={r.code}>
                  {r.reason} {fixLink(r.fixBy) ?? ca.missionUi.fixBy[r.fixBy] ?? ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="decide">
          {/* 1. The title: optional, and the platform's own when left empty. */}
          <section className="card form" aria-label={t.title}>
            <label>
              {t.title}
              <input
                name="title"
                maxLength={80}
                value={title}
                placeholder={t.titlePlaceholder}
                aria-describedby="title-help"
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <p id="title-help" className="muted small">
              {t.titleHelp}
            </p>
          </section>

          {/* 2. When it is played. */}
          <section className="card form" aria-label={ca.missionUi.when.title}>
            <h2>{ca.missionUi.when.title}</h2>
            <div className="row wrap">
              <label className="check">
                <input
                  type="radio"
                  name="when"
                  checked={whenMode === 'now'}
                  onChange={() => setWhenMode('now')}
                />
                {ca.missionUi.when.now}
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="when"
                  checked={whenMode === 'date'}
                  onChange={() => setWhenMode('date')}
                />
                {ca.missionUi.when.dateTime}
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="when"
                  checked={whenMode === 'onStart'}
                  onChange={() => setWhenMode('onStart')}
                />
                {ca.missionUi.when.onStart}
              </label>
            </div>
            {whenMode === 'date' && (
              <label>
                {ca.missionUi.page.scheduleAt}
                <input
                  type="datetime-local"
                  value={whenAt}
                  onChange={(e) => setWhenAt(e.target.value)}
                />
              </label>
            )}
            {whenMode === 'onStart' && (
              <p className="muted small">{ca.missionUi.when.onStartHint}</p>
            )}
            <p>
              <span className="muted small">{ca.missionUi.when.server}</span>{' '}
              <strong>{step1.serverName}</strong>{' '}
              <Link className="small" to="/new">
                {ca.missionUi.decisionTable.changeAtServer}
              </Link>
            </p>
          </section>

          {/* 2b. Who sees it: every member unless its author closes it. */}
          <section className="card form" aria-label={ca.missionUi.visibility.title}>
            <h2>{ca.missionUi.visibility.title}</h2>
            <div className="row wrap">
              <label className="check">
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'PUBLIC'}
                  onChange={() => setVisibility('PUBLIC')}
                />
                <i className="bi bi-people" aria-hidden="true" /> {ca.missionUi.visibility.group}
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'PRIVATE'}
                  onChange={() => setVisibility('PRIVATE')}
                />
                <i className="bi bi-lock" aria-hidden="true" /> {ca.missionUi.visibility.private}
              </label>
            </div>
            <p className="muted small">
              {visibility === 'PUBLIC'
                ? ca.missionUi.visibility.groupHint
                : ca.missionUi.visibility.privateHint}
            </p>
          </section>

          {/* 3. The hour and the weather: the type step's own answers, in the type's own range. */}
          <section className="card form" aria-label={t.skyTitle}>
            <h2>{t.skyTitle}</h2>
            <div className="sky">
              <div>
                {hourFixed ? (
                  <FixedValue
                    label={ca.decisionKeys.time}
                    value={t.hourFixed(hourLimit?.words ?? '')}
                    reason={hourLimit?.reason}
                  />
                ) : (
                  <>
                    <label>
                      {ca.decisionKeys.time}
                      <select
                        name="hour"
                        value={hour == null ? '' : String(hour)}
                        disabled={whoDecides('time') !== 'creator'}
                        onChange={(e) => {
                          setHourDropped(null);
                          setHour(e.target.value === '' ? null : Number(e.target.value));
                        }}
                      >
                        {/* A type that works its own hour out (night_raid: dawn less two and a
                            half hours) says so; any other hour it offers is one it keeps. */}
                        <option value="">
                          {recipe?.constraints?.hours ? ca.missionUi.hourFromType : ca.timeSeeded}
                        </option>
                        {hours.map((h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {hourDropped != null && (
                      <p className="notice warn small" role="status">
                        {ca.missionUi.typeDecides.hourReplaced(
                          hourLabel(hourDropped),
                          hourReason ?? '',
                        )}
                      </p>
                    )}
                    {hourLimit?.state === 'limited' && hourLimit.words ? (
                      <p className="muted small limit">
                        {t.hourLimited(hourLimit.words)} {hourLimit.reason}
                      </p>
                    ) : (
                      hourDropped == null &&
                      hourReason && <p className="muted small limit">{hourReason}</p>
                    )}
                    {whoDecides('time') !== 'creator' && (
                      <p className="muted small">{t.notCreators(valueFor('time', ''))}</p>
                    )}
                  </>
                )}
              </div>
              <div>
                {weatherFixed ? (
                  <FixedValue
                    label={ca.decisionKeys.weather}
                    value={weatherAsked ? weatherName(weatherAsked) : (weatherLimit?.words ?? '')}
                    reason={weatherLimit?.reason}
                  />
                ) : (
                  <>
                    <label>
                      {ca.decisionKeys.weather}
                      <select
                        name="weather"
                        value={weather ?? ''}
                        disabled={whoDecides('weather') !== 'creator'}
                        onChange={(e) => setWeather(e.target.value || null)}
                      >
                        <option value="">{ca.timeSeeded}</option>
                        {weathers.map((k) => (
                          <option key={k} value={k}>
                            {weatherName(k)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {weatherLimit?.state === 'limited' && weatherLimit.words && (
                      <p className="muted small limit">
                        {t.weatherLimited(weatherLimit.words)} {weatherLimit.reason}
                      </p>
                    )}
                    {whoDecides('weather') !== 'creator' && (
                      <p className="muted small">{t.notCreators(valueFor('weather', ''))}</p>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* The type's clock (ENGINE_DESIGN.md §2.8, §2.20): shown, never edited. */}
            {recipe && (
              <FixedValue
                label={t.clock}
                value={recipe.clock ?? ca.script.genericClock(recipe.minutes)}
              />
            )}
          </section>

          {/* 4. The supports. */}
          <section className="card form" aria-label={ca.supportsWhich}>
            <h2>{ca.supportsWhich}</h2>
            <p className="muted small">
              <span className="chip">
                {simplex
                  ? ca.missionUi.supportsSystem.simplex
                  : ca.missionUi.supportsSystem.vanilla}
              </span>
            </p>
            {supportsLimit?.state === 'limited' && supportsLimit.words && (
              <p className="muted small limit">{t.supportsLimited(supportsLimit.words)}</p>
            )}
            {supportsLimit?.state === 'fixed' && supportsLimit.values.length === 0 && (
              <FixedValue label={ca.supportsWhich} value={t.supportsNone} />
            )}
            <div className="two">
              {/* The type's own rules (release D, D5.9): a required or limited support is fixed, with its reason. */}
              {supportRules.map(([k, rule]) => {
                const system = ca.missionUi.supportsSystem;
                const name = system.ownNames[rule.entry ?? k] ?? supportName(k);
                return (
                  <label key={k} className="check fixed">
                    <input name={`support-${k}`} type="checkbox" checked disabled readOnly />
                    <span>
                      <i className="bi bi-lock" aria-hidden="true" />{' '}
                      {rule.limit != null
                        ? system.limited(name, rule.limit)
                        : `${name} · ${system.fixed}`}
                      {rule.reason && <span className="muted small block">{rule.reason}</span>}
                      {recipe?.gates?.[k] && (
                        <span className="muted small block">{ca.script.gate(recipe.gates[k])}</span>
                      )}
                    </span>
                  </label>
                );
              })}
              {/* The supports the type admits can be ticked (MECHANICS.md R17); the rest follow, padlocked. */}
              {offeredSupports.map((k) =>
                pieces?.supports[k] === false ? (
                  // Shown and off, with the reason, rather than gone: a creator who
                  // expects a drone is told why tonight has none.
                  <label key={k} className="check" title={ca.missionUi.supportsSystem.noPiece}>
                    <input
                      name={`support-${k}`}
                      type="checkbox"
                      checked={false}
                      disabled
                      readOnly
                    />
                    <span className="muted">
                      {supportName(k)} ·{' '}
                      {ca.missionUi.supportsSystem.why[pieces.supportReasons?.[k] ?? ''] ??
                        ca.missionUi.supportsSystem.noPieceShort}
                    </span>
                  </label>
                ) : (
                  // No number under either system (the owner, 2026-09-26): a support
                  // is there or it is not, and a cooldown between calls is what holds
                  // it back, on the game's own menu as under Simplex.
                  <label key={k} className="check">
                    <input
                      name={`support-${k}`}
                      type="checkbox"
                      checked={(supports?.[k] ?? 0) > 0}
                      onChange={(e) =>
                        setSupports((s) => ({ ...(s ?? {}), [k]: e.target.checked ? 1 : 0 }))
                      }
                    />
                    <span>
                      {supportName(k)}
                      {recipe?.gates?.[k] && (
                        <span className="muted small block">{ca.script.gate(recipe.gates[k])}</span>
                      )}
                    </span>
                  </label>
                ),
              )}
              {lockedSupports.map((k) => (
                <label key={k} className="check fixed">
                  <input name={`support-${k}`} type="checkbox" checked={false} disabled readOnly />
                  <span className="muted">
                    <i className="bi bi-lock" aria-hidden="true" /> {supportName(k)} ·{' '}
                    {ca.missionUi.supportsSystem.notAdmitted}
                  </span>
                </label>
              ))}
            </div>
            <HowItWorks>
              <p className="muted small">
                {simplex
                  ? ca.missionUi.supportsSystem.simplexHint
                  : ca.missionUi.supportsSystem.vanillaHint}
              </p>
              <p className="muted small">{ca.missionUi.supportsSystem.noPiece}</p>
            </HowItWorks>
          </section>

          {/* 5. The twists, and the type's own switches and choices. */}
          <section className="card form" aria-label={ca.missionUi.twists.title}>
            <div className="row between">
              <h2>{ca.missionUi.twists.title}</h2>
              {!noTwists && (
                <span className="muted small">
                  {chosenTwists.length === 0
                    ? ca.missionUi.twists.none
                    : ca.missionUi.twists.chosen(chosenTwists.length)}
                </span>
              )}
            </div>
            {noTwists ? (
              // A type that accepts no twist says so, with a lock, rather than showing an empty list.
              <FixedValue
                label={ca.missionUi.twists.title}
                value={t.twistsNone}
                reason={twistsLimit?.reason}
              />
            ) : (
              <p className="muted small">
                {ca.missionUi.twists.hint}{' '}
                {twistsLimit?.state === 'limited' && (
                  <span className="limit">{t.twistsLimited}</span>
                )}
              </p>
            )}
            {(offeredTwists.length > 0 || switches.length > 0) && (
              <ul className="twist-choices">
                {!noTwists &&
                  offeredTwists.map((tw) => {
                    const when = recipe?.optionals?.find(
                      (o) => o.kind === 'twist' && o.key === tw.id,
                    )?.window;
                    return (
                      <li key={tw.id}>
                        <label className="check">
                          <input
                            type="checkbox"
                            name={`twist-${tw.id}`}
                            checked={chosenTwists.includes(tw.id)}
                            onChange={() => toggleTwist(tw.id)}
                          />
                          <span>
                            <strong>{tw.title}</strong>
                            <span className="muted small block">{twistBody(tw.body)}</span>
                            {when && (
                              <span className="muted small block">
                                {ca.missionUi.twistWindow(when)}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                {/* The type's own switches (release D, D6.3): optional twists that are
                    off unless ticked here, never drawn by the seed. */}
                {switches.map((s) => (
                  <li key={`setting-${s.id}`}>
                    <label className="check">
                      <input
                        type="checkbox"
                        name={`setting-${s.id}`}
                        checked={settingValue(s, settings) === 'on'}
                        onChange={(e) => {
                          // Read now, not inside the updater: when another update is
                          // pending React runs the updater at render, after it has put
                          // the controlled box back, and the tick was read as "off".
                          const on = e.target.checked;
                          setSettings((c) => ({ ...c, [s.id]: on ? 'on' : 'off' }));
                        }}
                      />
                      <span>
                        <strong>{s.title}</strong>{' '}
                        <span className="chip">{ca.missionUi.settings.switchChip}</span>
                        <span className="muted small block">{s.blurb}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {switchedOn.length > 0 && (
              <p className="small">{ca.missionUi.settings.switchedOn(switchedOn.join(', '))}</p>
            )}
          </section>
          {choices.length > 0 && (
            <section className="card form" aria-label={ca.missionUi.settings.choicesTitle}>
              <h2>{ca.missionUi.settings.choicesTitle}</h2>
              <p className="muted small">{ca.missionUi.settings.choicesHint}</p>
              {choices.map((s) => (
                <label key={s.id}>
                  {s.title}
                  <select
                    name={`setting-${s.id}`}
                    value={settingValue(s, settings)}
                    onChange={(e) => setSettings((c) => ({ ...c, [s.id]: e.target.value }))}
                  >
                    {s.values.map((v) => (
                      <option key={v} value={v}>
                        {(s.valueTitles[v] ?? v) +
                          (v === s.default ? ` ${ca.missionUi.settings.byDefault}` : '')}
                      </option>
                    ))}
                  </select>
                  {s.blurb && <span className="muted small block">{s.blurb}</span>}
                </label>
              ))}
            </section>
          )}

          {/* 6. The votes, folded: who decides what, for the rows that can be voted. */}
          <section className="card form votes" aria-label={t.votesTitle}>
            <details>
              <summary>
                <h2>{t.votesTitle}</h2>
              </summary>
              <p className="muted small">{t.votesHelp}</p>
              <div className="matrix">
                {voteRows.map((r) => {
                  const current: By = isLive(r.key) ? whoDecides(r.key) : 'creator';
                  return (
                    <div className="mrow" key={r.key}>
                      <span className="label">{r.label}</span>
                      <div className="options" role="radiogroup" aria-label={r.label}>
                        {(r.key === 'twist' ? TWIST_BY : BY).map((k) => (
                          <button
                            type="button"
                            key={k}
                            role="radio"
                            aria-checked={current === k}
                            className={`chip toggle ${current === k ? 'on' : ''}`}
                            onClick={() => setBy((b) => ({ ...b, [r.key]: k }))}
                          >
                            {ca.decidedBy[k]}
                          </button>
                        ))}
                      </div>
                      <span className="value">{r.value}</span>
                    </div>
                  );
                })}
                {plannedVotes.map((r) => (
                  <div className="mrow" key={r.key}>
                    <span className="label">
                      {r.label} <PlannedChip id={r.plannedId} />
                    </span>
                    <div className="options" role="radiogroup" aria-label={r.label}>
                      {BY.map((k) => (
                        <button
                          type="button"
                          key={k}
                          role="radio"
                          aria-checked={k === 'creator'}
                          aria-disabled
                          className={`chip toggle ${k === 'creator' ? 'on' : ''}`}
                        >
                          {ca.decidedBy[k]}
                        </button>
                      ))}
                    </div>
                    <span className="value">{r.value}</span>
                  </div>
                ))}
                {plannedRows.map((r) => (
                  <div className="mrow" key={r.key}>
                    <span className="label">
                      {r.label} <PlannedChip id={r.plannedId} />
                    </span>
                    <span className="value">{r.value}</span>
                  </div>
                ))}
              </div>
              {fixedNames.length > 0 && (
                <p className="muted small">
                  <i className="bi bi-lock" aria-hidden="true" />{' '}
                  {t.votesFixed(fixedNames.join(', ').toLowerCase())}
                </p>
              )}
              <p className="muted small">{ca.decisionsHint}</p>
            </details>
          </section>

          {/* 7. The whole mission in a line, what earlier steps decided, and the button. */}
          <section className="card form summary" aria-label={t.summaryTitle}>
            <h2>{t.summaryTitle}</h2>
            <p className="summary-line">{summaryLine}</p>
            <ul className="plain elsewhere">
              {elsewhere.map((r) => (
                <li key={r.key}>
                  <span className="muted small">{r.label}:</span> {r.value}{' '}
                  {r.change && (
                    <Link to={r.change.to} className="small">
                      {r.change.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            {generated && (
              <section
                className="notice warn"
                role="status"
                aria-label={ca.missionUi.generated.title}
              >
                <strong>
                  {generated.warnings.length > 0
                    ? ca.missionUi.generated.title
                    : ca.missionUi.generated.titleOnly}
                </strong>
                {generated.warnings.length > 0 && (
                  <p className="small">{ca.missionUi.generated.hint}</p>
                )}
                {generated.uninvited.length > 0 && (
                  <p className="small">
                    {ca.missionUi.editors.notInvited(
                      generated.uninvited.map((h) => `@${h}`).join(', '),
                    )}
                  </p>
                )}
                <ul>
                  {generated.warnings.map((w) => (
                    <li key={w}>
                      <WarningLine code={w} />
                    </li>
                  ))}
                </ul>
                <Link className="button primary" to={generated.to}>
                  {generated.session
                    ? ca.missionUi.generated.openSession
                    : ca.missionUi.generated.openMission}
                </Link>
              </section>
            )}
            <div className="row end">
              <button
                type="button"
                className="primary"
                disabled={busy || generated != null || !recipe || recipe.compatible === false}
                onClick={() => void generate()}
              >
                {busy ? t.creating : t.create}
              </button>
            </div>
          </section>
        </div>
      </QuickStart>
    </Shell>
  );
}
